import { Control, useWatch } from 'react-hook-form';
import {
    IJobQuestionsForm,
    JobQuestionObjectFields,
} from '../../../../../../common';
import { Radio, useTheme, Typography } from '../../../../../../base';
import {
    StyledOptionsContainer,
    StyledOptionsListWrapper,
    StyledOptionItemWrapper,
    StyledOptionItemContent,
} from '../../JobQuestions.styles';
import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';

interface QuestionBooleanOptionsProps {
    fieldNamePrefix: `questions.${number}`;
    control: Control<IJobQuestionsForm>;
    isDealbreaker?: boolean;
    hasBeenSubmitted?: boolean;
    dealbreakerAnswerError?: FieldError;
    onDealbreakerAnswerChange?: (answer: 'true' | 'false') => void;
}

export const QuestionBooleanOptions = ({
    fieldNamePrefix,
    control,
    isDealbreaker,
    hasBeenSubmitted,
    dealbreakerAnswerError,
    onDealbreakerAnswerChange,
}: QuestionBooleanOptionsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const dealbreakerAnswer = useWatch({
        control,
        name: `${fieldNamePrefix}.${JobQuestionObjectFields.DEALBREAKER_ANSWER}` as `questions.${number}.dealbreakerAnswer`,
    });

    const radioSx = {
        padding: 0,
        '& .MuiSvgIcon-root': {
            fontSize: 18,
        },
        '&.Mui-disabled': {
            color: theme.palette.grey[400],
        },
    };

    const showError =
        hasBeenSubmitted && isDealbreaker && !!dealbreakerAnswerError;

    return (
        <StyledOptionsContainer>
            <StyledOptionsListWrapper>
                <StyledOptionItemWrapper>
                    <StyledOptionItemContent>
                        <Radio
                            checked={dealbreakerAnswer === 'true'}
                            onChange={() =>
                                onDealbreakerAnswerChange?.('true')
                            }
                            disabled={!isDealbreaker}
                            size="small"
                            sx={radioSx}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDealbreaker
                                    ? theme.palette.text.primary
                                    : theme.palette.text.disabled,
                            }}
                        >
                            {t('common.true')}
                        </Typography>
                    </StyledOptionItemContent>
                </StyledOptionItemWrapper>
                <StyledOptionItemWrapper>
                    <StyledOptionItemContent>
                        <Radio
                            checked={dealbreakerAnswer === 'false'}
                            onChange={() =>
                                onDealbreakerAnswerChange?.('false')
                            }
                            disabled={!isDealbreaker}
                            size="small"
                            sx={radioSx}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDealbreaker
                                    ? theme.palette.text.primary
                                    : theme.palette.text.disabled,
                            }}
                        >
                            {t('common.false')}
                        </Typography>
                    </StyledOptionItemContent>
                </StyledOptionItemWrapper>
            </StyledOptionsListWrapper>
            {showError && (
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.error.main,
                        display: 'block',
                        marginTop: theme.spacing(0.5),
                    }}
                >
                    {dealbreakerAnswerError?.message ||
                        t('saveJob.dealbreakerAnswerRequired')}
                </Typography>
            )}
        </StyledOptionsContainer>
    );
};
